import {useMutation, UseMutationResult, useQuery} from '@tanstack/react-query';
import {
  addProject,
  EditProjectDetails,
  fetchByHcode,
  fetchProjectDomainData,
  getCreateProjectMeta,
  getDomainDashboardUsers,
  getDomainData,
  getDomainUsers,
  getEntityList,
  getLongSortLimit,
  getManageUsers,
  getMaxLimit,
  getProjectList,
  getProjects,
  getProjectTemplate,
  getProjectUsers,
  IUserDetailsRequest,
  IUserDetailsResponse,
  ProjectListResponse,
  updateDomainUsers,
  updateProjectUsers,
} from '../../api/endpoints/projectApi';
import {
  AddProjectPayload,
  DomainUsers,
  EntityItem,
  GetHcodeParams,
  IAssignUsersResponse,
  IProjectsListResponse,
  IUpdateDomainPayload,
  IUpdateProjectPayload,
  ManageUser,
} from '../../api/types/project.type';
import {SourceTypeCode} from '../../shared/constants';

export const useManageUsers = (projectId: number) => {
  return useQuery<ManageUser, Error>({
    queryKey: ['manageUser', projectId],
    queryFn: () => getManageUsers(projectId),
  });
};

export const useDomainUsers = (domainId: number | undefined) => {
  return useQuery<DomainUsers, Error>({
    queryKey: ['domainUsers', domainId],
    queryFn: () => getDomainUsers(domainId!),
    enabled: !!domainId,
    staleTime: 0,
    // keepPreviousData: false
  });
};

export const useProjectUsers = (projectId: number | undefined) => {
  return useQuery<DomainUsers, Error>({
    queryKey: ['projectUsers', projectId],
    queryFn: () => getProjectUsers(projectId!),
    enabled: !!projectId,
  });
};

export const useUpdateDomainUsers = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IAssignUsersResponse, Error, IUpdateDomainPayload>({
    mutationFn: updateDomainUsers,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateProjectUsers = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
): UseMutationResult<IAssignUsersResponse, Error, IUpdateProjectPayload> => {
  return useMutation({
    mutationFn: updateProjectUsers,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetProjectList = (
  clientTypeId: number,
  clientId: number,
  type: string
) => {
  return useQuery<ProjectListResponse, Error>({
    queryKey: ['projectList', clientTypeId, clientId],
    queryFn: () => getProjectList(clientTypeId, clientId),
    enabled: type !== 'edit',
  });
};

export const useGetEntityList = (domainType: number) => {
  return useQuery<EntityItem[], Error>({
    queryKey: ['entityList', domainType],
    queryFn: () => getEntityList(domainType),
    enabled: !!domainType,
  });
};

export const useDomainData = (entityId: number) => {
  return useQuery({
    queryKey: ['domain-data', entityId],
    queryFn: () => getDomainData(entityId),
    enabled: !!entityId,
  });
};

export const useProjects = (queryParams: string) => {
  return useQuery<IProjectsListResponse, Error>({
    queryKey: ['projects', queryParams],
    queryFn: () => getProjects(queryParams),
  });
};

export const useAlphaCodeCheck = (params: GetHcodeParams, enabled = false) => {
  return useQuery({
    queryKey: ['alpha-code-check', params],
    queryFn: () => fetchByHcode(params),
    enabled: !!params?.hcode && !!params?.selectedEntityId && enabled,
    retry: false,
  });
};

export const useProjectDomainData = (entityId: number) => {
  return useQuery({
    queryKey: ['projectDomainData', entityId],
    queryFn: () => fetchProjectDomainData(entityId),
    enabled: !!entityId,
  });
};

export const useAddProject = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation({
    mutationFn: (payload: AddProjectPayload) => addProject(payload),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const useDomainDashboardUsers = (payload: IUserDetailsRequest) => {
  return useQuery<IUserDetailsResponse[], Error>({
    queryKey: ['domainDashboardUsers', payload],
    queryFn: () => getDomainDashboardUsers(payload),
    retry: 2,
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });
};

export const useEditProjectDetails = (projectId: string) => {
  return useQuery({
    queryKey: ['editProjectDetails', projectId],
    queryFn: () => EditProjectDetails(projectId),
    enabled: projectId.trim() !== '',
  });
};

export const useCreateProjectMeta = (type: string, roleId: number) => {
  return useQuery({
    queryKey: ['createProjectMeta'],
    queryFn: () => getCreateProjectMeta(),
    enabled: type !== 'edit' && roleId === -1,
  });
};

export const useDocViewLimit = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['docViewLimit'],
    queryFn: () => getMaxLimit(SourceTypeCode.DOCVIEW),
    enabled,
  });
};

export const useDocListLimit = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['docListLimit'],
    queryFn: () => getMaxLimit(SourceTypeCode.DOCLIST),
    enabled,
  });
};

export const useDatasetDocViewLimit = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['datasetDocViewLimit'],
    queryFn: () => getMaxLimit('DATASETDOCVIEW'),
    enabled,
  });
};

export const useDatasetDocListLimit = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['datasetDocListLimit'],
    queryFn: () => getMaxLimit('DATASETDOCLIST'),
    enabled,
  });
};

export const useLongSortLimit = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['docLongSortLimit'],
    queryFn: () => getLongSortLimit(),
    enabled,
  });
};

export const useGetProjectTemplates = (projectId: string, type: string) => {
  return useQuery({
    queryKey: ['getProjectTemplates', projectId],
    queryFn: () => getProjectTemplate(projectId),
    enabled: type !== 'edit' && !!projectId,
  });
};
